import React, { useEffect, useState } from 'react';
import { createPrize, deletePrize, onPrizes, updatePrize } from '../../services/tripleCrown';
import { ordinal } from '../../utils/championshipDates';
import { PRIZE_TYPES, cleanPrizeItems, prizeParts, prizePartText, prizeSummary } from '../../utils/prizes';
import S3Upload from '../S3Upload';

/* ══════════════════════════════════════
   PRIZES — per finishing position: any mix of cash, Whapaxx credits and
   physical prizes, plus an optional picture and an extra line.
   ══════════════════════════════════════ */

const blankRow = (type = 'cash') => ({ type, amount: '', name: '' });

const medal = pos => (pos === 1 ? '🥇' : pos === 2 ? '🥈' : pos === 3 ? '🥉' : `#${pos}`);
const medalColor = pos => (pos === 1 ? 'var(--gold)' : pos === 2 ? 'var(--text-soft)' : pos === 3 ? '#cd7f32' : 'var(--text)');
const typeOf = key => PRIZE_TYPES.find(t => t.key === key) || PRIZE_TYPES[2];

/** The editor's rows for an existing prize (an old text-only prize becomes one item). */
function rowsFor(prize) {
  const parts = prizeParts(prize);
  if (parts.length) return parts.map(p => ({ ...blankRow(p.type), ...p, amount: p.amount ?? '', name: p.name ?? '' }));
  if (prize?.description) return [{ ...blankRow('item'), name: prize.description }];
  return [blankRow('cash')];
}

export default function PrizesSection({ championshipId, setToast }) {
  const [prizes, setPrizes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);        // the prize being edited, or null for new
  const [position, setPosition] = useState('');
  const [rows, setRows] = useState([blankRow()]);
  const [image, setImage] = useState('');
  const [details, setDetails] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => onPrizes(championshipId, setPrizes), [championshipId]);

  const sorted = [...prizes].sort((a, b) => (a.position || 0) - (b.position || 0));
  const items = cleanPrizeItems(rows);
  const taken = sorted.find(p => p.position === parseInt(position, 10) && (!editing || p.id !== editing.id));

  const startNew = () => {
    const next = (sorted.reduce((m, p) => Math.max(m, p.position || 0), 0) || 0) + 1;
    setEditing(null); setPosition(next); setRows([blankRow()]); setImage(''); setDetails(''); setOpen(true);
  };
  const startEdit = (p) => {
    setEditing(p); setPosition(p.position); setRows(rowsFor(p));
    setImage(p.image || ''); setDetails(p.details || ''); setOpen(true);
  };
  const close = () => { setOpen(false); setEditing(null); };

  const setRow = (i, patch) => setRows(rs => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const removeRow = i => setRows(rs => (rs.length > 1 ? rs.filter((_, j) => j !== i) : [blankRow()]));

  const save = async () => {
    const pos = parseInt(position, 10);
    if (!pos || pos < 1 || !items.length) return;
    setSaving(true);
    try {
      const data = {
        championship: championshipId,
        position: pos,
        items,
        image: image || null,
        details: details.trim() || null,
        description: prizeSummary({ items }),   // the old one-line field, kept for older readers
      };
      if (editing) { await updatePrize(editing.id, data); setToast({ type: 'success', msg: 'Prize updated!' }); }
      else { await createPrize(data); setToast({ type: 'success', msg: 'Prize added!' }); }
      close();
    } catch (err) {
      setToast({ type: 'error', msg: err.message || 'Failed to save prize' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ marginTop: 18 }}>
      <div className="card-header">
        <h3 className="card-title">Prizes ({prizes.length})</h3>
        <button className="btn btn-primary btn-sm" onClick={() => (open && !editing ? close() : startNew())}>
          {open && !editing ? 'Cancel' : '+ Add Prize'}
        </button>
      </div>

      {open && (
        <div style={{ padding: '0 20px 18px', borderBottom: '1px solid var(--border)', marginBottom: 14 }}>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div className="form-group" style={{ margin: 0, width: 90 }}>
              <label className="form-label">Position *</label>
              <input className="form-input" type="number" min="1" value={position}
                onChange={e => setPosition(parseInt(e.target.value, 10) || '')} />
            </div>

            <div style={{ flex: 1, minWidth: 300 }}>
              <label className="form-label">What they win *</label>
              {rows.map((r, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                  <select className="form-select" style={{ width: 170 }} value={r.type}
                    onChange={e => setRow(i, { type: e.target.value })}>
                    {PRIZE_TYPES.map(t => <option key={t.key} value={t.key}>{t.icon} {t.label}</option>)}
                  </select>
                  {r.type === 'item' ? (
                    <input className="form-input" style={{ flex: 1 }} value={r.name}
                      placeholder="e.g. Logitech G29 racing wheel, trophy, headset"
                      onChange={e => setRow(i, { name: e.target.value })} />
                  ) : (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {r.type === 'cash' && <span style={{ color: 'var(--text-muted)' }}>$</span>}
                      <input className="form-input" type="number" min="0" step={r.type === 'cash' ? '0.01' : '1'}
                        value={r.amount} placeholder={r.type === 'cash' ? '500' : '2000'}
                        onChange={e => setRow(i, { amount: e.target.value })} />
                      {r.type === 'credits' && <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>credits</span>}
                    </div>
                  )}
                  <button className="btn btn-secondary btn-sm" type="button" title="Remove" onClick={() => removeRow(i)}>✕</button>
                </div>
              ))}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {PRIZE_TYPES.map(t => (
                  <button key={t.key} type="button" className="btn btn-secondary btn-sm"
                    onClick={() => setRows(rs => [...rs, blankRow(t.key)])}>+ {t.label}</button>
                ))}
              </div>
            </div>

            <div className="form-group" style={{ margin: 0, width: 200 }}>
              <label className="form-label">Picture (optional)</label>
              <S3Upload
                key={editing ? editing.id : 'new'}
                folder={`championships/media/prizes/${championshipId}`}
                accept="image/*"
                label={image ? 'Replace picture' : 'Upload picture'}
                currentUrl={image}
                onUploaded={({ publicUrl }) => setImage(publicUrl)}
              />
              {image && (
                <button type="button" className="btn btn-secondary btn-sm" style={{ marginTop: 6 }}
                  onClick={() => setImage('')}>Remove picture</button>
              )}
            </div>
          </div>

          <div className="form-group" style={{ margin: '12px 0 0' }}>
            <label className="form-label">Extra details (optional)</label>
            <input className="form-input" value={details} maxLength={200}
              placeholder="e.g. Presented at the finale · credits added to the winner's account"
              onChange={e => setDetails(e.target.value)} />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
            <button className="btn btn-primary btn-sm" disabled={saving || !items.length || !position}
              onClick={save}>{saving ? 'Saving…' : editing ? 'Update prize' : 'Add prize'}</button>
            {editing && <button className="btn btn-secondary btn-sm" onClick={close}>Cancel</button>}
            {items.length > 0 && (
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                Shows as: <strong style={{ color: 'var(--text)' }}>{medal(parseInt(position, 10) || 0)} {prizeSummary({ items })}</strong>
              </span>
            )}
            {taken && (
              <span style={{ fontSize: 12, color: 'var(--orange, #f59e0b)' }}>
                {ordinal(taken.position)} already has a prize — this adds a second one.
              </span>
            )}
          </div>
        </div>
      )}

      {sorted.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0, padding: '0 20px 16px' }}>
          No prizes yet — add prizes to display on the championship page.
        </p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th style={{ width: 90 }}>Position</th><th style={{ width: 70 }}></th><th>Prize</th><th style={{ width: 140 }}></th></tr>
            </thead>
            <tbody>
              {sorted.map(p => {
                const parts = prizeParts(p);
                return (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 700, color: medalColor(p.position) }}>
                      {medal(p.position)} {ordinal(p.position)}
                    </td>
                    <td>
                      {p.image ? (
                        <img src={p.image} alt="" style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)' }} />
                      ) : null}
                    </td>
                    <td>
                      {parts.length ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                          {parts.map((part, i) => (
                            <span key={i}>{typeOf(part.type).icon} {prizePartText(part)}</span>
                          ))}
                        </div>
                      ) : p.description}
                      {p.details && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{p.details}</div>}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="btn btn-secondary btn-sm" style={{ marginRight: 6 }} onClick={() => startEdit(p)}>Edit</button>
                      <button className="btn btn-danger btn-sm"
                        onClick={async () => {
                          if (window.confirm(`Delete the ${ordinal(p.position)} place prize?`)) {
                            await deletePrize(p.id);
                            setToast({ type: 'success', msg: 'Prize deleted' });
                          }
                        }}>Delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
